// Cost Service API Client

import { 
  CostSummary, 
  CostTrend, 
  ModelUsage, 
  UserUsage, 
  ModelPricing, 
  CostAlerts,
  QueryParams,
  ExportParams 
} from '../types/cost';
import { authenticatedFetch } from '../utils/authApi';

const BASE_URL = '/api/cost';

class CostService {
  private async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    // Use our authenticated fetch helper
    const response = await authenticatedFetch(url, options);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response;
  }

  private buildQueryString(params: Record<string, any>): string {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value != null && value !== '')
    );
    
    return new URLSearchParams(cleanParams).toString();
  }

  async getSummary(params: QueryParams = {}): Promise<CostSummary> {
    const queryString = this.buildQueryString(params);
    const url = `${BASE_URL}/summary${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.fetchWithAuth(url);
    return response.json();
  }

  async getTrends(params: QueryParams = {}): Promise<CostTrend[]> {
    const queryString = this.buildQueryString(params);
    const url = `${BASE_URL}/trends${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.fetchWithAuth(url);
    return response.json();
  }

  async getModelBreakdown(params: QueryParams = {}): Promise<ModelUsage[]> {
    const queryString = this.buildQueryString(params);
    const url = `${BASE_URL}/by-model${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.fetchWithAuth(url);
    return response.json();
  }

  async getUserBreakdown(params: QueryParams = {}): Promise<UserUsage[]> {
    const queryString = this.buildQueryString(params);
    const url = `${BASE_URL}/by-user${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.fetchWithAuth(url);
    return response.json();
  }

  async getModelPricing(): Promise<ModelPricing[]> {
    const response = await this.fetchWithAuth(`${BASE_URL}/models`);
    return response.json();
  }

  async getCostAlerts(): Promise<CostAlerts> {
    const response = await this.fetchWithAuth(`${BASE_URL}/alerts`);
    return response.json();
  }

  async exportData(params: ExportParams = {}): Promise<void> {
    const queryString = this.buildQueryString(params);
    const url = `${BASE_URL}/export${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.fetchWithAuth(url);
    
    // Handle CSV download
    if (params.format === 'csv' || !params.format) {
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'openai_usage_export.csv';
      
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    }
  }

  async checkHealth(): Promise<{ status: string; service: string; timestamp: string }> {
    const response = await this.fetchWithAuth(`${BASE_URL}/health`);
    return response.json();
  }
}

export const costService = new CostService();
